import { useEffect, useMemo, useState } from 'react';
import { AdminLayout } from '@/components/AdminLayout';
import { Card, CardContent } from '@/components/ui/card';
import { api } from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Loader2, XCircle, ExternalLink, Video, Search, Copy, Mail, Plus, Calendar as CalendarIcon, Star, MessageSquare, ThumbsUp, ThumbsDown, RotateCcw, UserX, CalendarClock, Download } from 'lucide-react';
import { canEdit as canEditSection, canViewSub, isSubHidden } from '@/lib/auth';
import { formatDateTime } from '@/lib/format';

const CATEGORY_OPTIONS = [
    { value: 'upcoming', label: 'Upcoming' },
    { value: 'completed', label: 'Completed' },
    { value: 'mentor_cancelled', label: 'Mentor cancelled' },
    { value: 'candidate_cancelled', label: 'Candidate cancelled' },
    { value: 'mentor_rescheduled', label: 'Mentor rescheduled' },
    { value: 'candidate_rescheduled', label: 'Candidate rescheduled' },
];

/* Status + party badges for a booking row/detail. */
function StatusBadges({ b }) {
    return (
        <div className="flex flex-wrap items-center gap-1">
            {b.status === 'scheduled' && <Badge className="bg-primary/90">Upcoming</Badge>}
            {b.status === 'completed' && <Badge variant="secondary">Completed</Badge>}
            {b.status === 'cancelled' && (
                <Badge variant="outline" className="gap-1 border-destructive/40 text-destructive">
                    <UserX className="h-3 w-3" />
                    {b.cancelled_by === 'mentor' ? 'Mentor cancelled' : 'Candidate cancelled'}
                </Badge>
            )}
            {b.rescheduled_by && (
                <Badge variant="outline" className="gap-1 border-amber-500/40 text-amber-600">
                    <CalendarClock className="h-3 w-3" />
                    {b.rescheduled_by === 'mentor' ? 'Mentor rescheduled' : 'Candidate rescheduled'}
                </Badge>
            )}
            {b.feedback_submitted && <Badge variant="secondary">Feedback ✓</Badge>}
        </div>
    );
}

/* Render a mentor feedback answer based on its field type (rating → stars). */
function renderAnswer(field, value) {
    if (value === undefined || value === null || value === '') {
        return <span className="text-muted-foreground">—</span>;
    }
    if (field?.type === 'rating') {
        return (
            <span className="inline-flex items-center gap-0.5 text-amber-500" aria-label={`${value} out of 5`}>
                {[1, 2, 3, 4, 5].map((n) => (
                    <Star key={n} className={`h-4 w-4 ${n <= Number(value) ? 'fill-current' : 'opacity-30'}`} />
                ))}
            </span>
        );
    }
    if (field?.type === 'yesno') return <span className="capitalize">{String(value)}</span>;
    return <span className="whitespace-pre-wrap">{String(value)}</span>;
}

/* Star row for the student's numeric rating. */
function StarRating({ value }) {
    return (
        <span className="inline-flex items-center gap-0.5 text-amber-500" aria-label={`${value} out of 5`}>
            {[1, 2, 3, 4, 5].map((n) => (
                <Star key={n} className={`h-4 w-4 ${n <= Number(value) ? 'fill-current' : 'opacity-30'}`} />
            ))}
        </span>
    );
}

export default function AdminBookingsPage() {
    const [bookings, setBookings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [category, setCategory] = useState('all');
    const [q, setQ] = useState('');
    const [selected, setSelected] = useState(null);
    const [feedback, setFeedback] = useState(null);
    const [feedbackLoading, setFeedbackLoading] = useState(false);
    const [manualOpen, setManualOpen] = useState(false);
    const [cancelTarget, setCancelTarget] = useState(null);
    const [rescheduleTarget, setRescheduleTarget] = useState(null);
    const canEdit = canEditSection('bookings');

    // Category filter options limited to what this admin is allowed to see.
    const allowedCategories = useMemo(
        () => CATEGORY_OPTIONS.filter((o) => canViewSub('bookings', o.value)),
        [],
    );

    // Fetch this session's feedback (mentor + student) whenever a booking is opened.
    useEffect(() => {
        if (!selected?.id) {
            setFeedback(null);
            return;
        }
        let cancelled = false;
        setFeedback(null);
        setFeedbackLoading(true);
        (async () => {
            try {
                const { data } = await api.get(`/admin/bookings/${selected.id}/feedback`);
                if (!cancelled) setFeedback(data);
            } catch (e) {
                if (!cancelled) setFeedback({ error: true });
            } finally {
                if (!cancelled) setFeedbackLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [selected?.id]);

    const refresh = async () => {
        setLoading(true);
        try {
            const params = {};
            if (category !== 'all') params.category = category;
            if (q) params.q = q;
            const { data } = await api.get('/admin/bookings', { params });
            setBookings(data.bookings || []);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        refresh();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [category]);

    const feedbackLink = (b) => `${window.location.origin}/mentor/feedback/${b.id}`;

    const emailFeedbackRequest = async (b) => {
        if (!b.mentor_email) {
            toast.error('No mentor email on file. Add one under Mentors → Edit card first.');
            return;
        }
        if (!window.confirm(`Email a feedback request to ${b.mentor_name} (${b.mentor_email})?`)) return;
        try {
            const { data } = await api.post(`/admin/bookings/${b.id}/send-mentor-feedback-request`);
            toast.success(`Feedback request sent to ${data.sent_to}`);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to send.');
        }
    };

    return (
        <AdminLayout title="Bookings" subtitle="All past and upcoming 1:1 sessions">
            <Card className="mb-4">
                <CardContent className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center">
                    <div className="relative flex-1">
                        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                        <Input
                            className="pl-9"
                            placeholder="Search by student, mentor, college ID…"
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && refresh()}
                        />
                    </div>
                    <Select value={category} onValueChange={setCategory}>
                        <SelectTrigger className="w-full sm:w-52" data-testid="admin-bookings-category">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All bookings</SelectItem>
                            {allowedCategories.map((o) => (
                                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <Button variant="secondary" onClick={refresh}>Refresh</Button>
                    {canEdit && (
                        <Button
                            data-testid="admin-add-booking-button"
                            onClick={() => setManualOpen(true)}
                        >
                            <Plus className="mr-1 h-4 w-4" /> Add booking
                        </Button>
                    )}
                </CardContent>
            </Card>

            <Card>
                <CardContent className="p-0">
                    {loading ? (
                        <div className="flex items-center gap-2 p-6 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
                    ) : bookings.length === 0 ? (
                        <div className="p-10 text-center text-sm text-muted-foreground">No bookings yet.</div>
                    ) : (
                        <div className="overflow-x-auto">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>When</TableHead>
                                        <TableHead>Mentor</TableHead>
                                        <TableHead>Student</TableHead>
                                        <TableHead>College ID</TableHead>
                                        <TableHead>Duration</TableHead>
                                        <TableHead>Status</TableHead>
                                        <TableHead>Overall Rating</TableHead>
                                        <TableHead className="text-right">Actions</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {bookings.map((b) => (
                                        <TableRow key={b.id}>
                                            <TableCell className="text-sm">{formatDateTime(b.start_iso)}</TableCell>
                                            <TableCell className="text-sm font-medium">{b.mentor_name}</TableCell>
                                            <TableCell className="text-sm">
                                                <div>{b.student_name}</div>
                                                <div className="text-xs text-muted-foreground">{b.student_email}</div>
                                            </TableCell>
                                            <TableCell className="text-sm">{b.student_college_id}</TableCell>
                                            <TableCell className="text-sm tabular-nums">{b.duration_minutes} min</TableCell>
                                            <TableCell>
                                                <StatusBadges b={b} />
                                            </TableCell>
                                            <TableCell data-testid="booking-rating-cell">
                                                {b.mentor_overall_rating ? (
                                                    <span className="inline-flex items-center gap-1" title={`Mentor's overall rating of the student: ${b.mentor_overall_rating}/5`}>
                                                        <StarRating value={b.mentor_overall_rating} />
                                                        <span className="text-xs tabular-nums text-muted-foreground">{Number(b.mentor_overall_rating).toFixed(1)}</span>
                                                    </span>
                                                ) : null}
                                            </TableCell>
                                            <TableCell className="text-right">
                                                <Button variant="ghost" size="sm" onClick={() => setSelected(b)}>Details</Button>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Details dialog */}
            <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
                <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-auto">
                    <DialogHeader>
                        <DialogTitle className="font-display">Booking details</DialogTitle>
                    </DialogHeader>
                    {selected && (
                        <div className="space-y-3 text-sm">
                            <div><strong>When:</strong> {formatDateTime(selected.start_iso)} · {selected.duration_minutes} min</div>
                            <div><strong>Mentor:</strong> {selected.mentor_name}</div>
                            <div><strong>Student:</strong> {selected.student_name} ({selected.student_email})</div>
                            <div><strong>College ID:</strong> {selected.student_college_id}</div>
                            {selected.domain && <div><strong>Domain:</strong> {selected.domain}</div>}
                            {selected.topic && <div><strong>Topic:</strong> {selected.topic}</div>}
                            {selected.cv_id && (
                                <div className="flex items-center gap-2">
                                    <strong>CV:</strong>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        data-testid="admin-download-cv"
                                        onClick={async () => {
                                            try {
                                                const res = await api.get(`/admin/bookings/${selected.id}/cv`, { responseType: 'blob' });
                                                const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                                                const a = document.createElement('a');
                                                a.href = url;
                                                a.download = selected.cv_filename || 'candidate-cv.pdf';
                                                document.body.appendChild(a);
                                                a.click();
                                                a.remove();
                                                window.URL.revokeObjectURL(url);
                                            } catch (e) {
                                                toast.error('Could not download the CV.');
                                            }
                                        }}
                                    >
                                        <Download className="mr-1.5 h-3.5 w-3.5" /> {selected.cv_filename || 'Download CV'}
                                    </Button>
                                </div>
                            )}
                            <div className="flex items-center gap-2"><strong>Status:</strong> <StatusBadges b={selected} /></div>
                            {selected.credit_refunded !== undefined && (
                                <div><strong>Credit refunded:</strong> {selected.credit_refunded ? 'Yes' : 'No'}</div>
                            )}
                            {canEdit && selected.status === 'scheduled' && (
                                <div className="flex flex-wrap gap-2 pt-1">
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => setRescheduleTarget(selected)}
                                        data-testid="admin-booking-reschedule"
                                    >
                                        <RotateCcw className="mr-1 h-3.5 w-3.5" /> Reschedule
                                    </Button>
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        className="border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive"
                                        onClick={() => setCancelTarget(selected)}
                                        data-testid="admin-booking-cancel"
                                    >
                                        <XCircle className="mr-1 h-3.5 w-3.5" /> Cancel
                                    </Button>
                                </div>
                            )}
                            <div className="flex flex-wrap gap-2 pt-2">
                                {selected.meet_link && (
                                    <a href={selected.meet_link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs hover:bg-accent">
                                        <Video className="h-3.5 w-3.5" /> Meet
                                    </a>
                                )}
                                {selected.google_event_link && (
                                    <a href={selected.google_event_link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs hover:bg-accent">
                                        <ExternalLink className="h-3.5 w-3.5" /> Calendar event
                                    </a>
                                )}
                                <button
                                    className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs hover:bg-accent"
                                    onClick={() => {
                                        navigator.clipboard.writeText(feedbackLink(selected));
                                        toast.success('Feedback link copied');
                                    }}
                                >
                                    <Copy className="h-3.5 w-3.5" /> Copy mentor feedback link
                                </button>
                                <button
                                    data-testid="admin-booking-email-feedback"
                                    className="inline-flex items-center gap-1 rounded-md border border-primary/40 bg-primary/5 px-2 py-1 text-xs text-primary hover:bg-primary/10 disabled:opacity-50"
                                    onClick={() => emailFeedbackRequest(selected)}
                                    disabled={!selected.mentor_email}
                                    title={selected.mentor_email ? `Send to ${selected.mentor_email}` : 'Add mentor email under Mentors → Edit card first'}
                                >
                                    <Mail className="h-3.5 w-3.5" /> Email feedback request
                                </button>
                            </div>

                            {/* Session feedback (mentor + student) */}
                            <div className="space-y-3 border-t border-border/60 pt-3" data-testid="admin-booking-feedback-section">
                                <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                                    <MessageSquare className="h-3.5 w-3.5" /> Session feedback
                                </div>

                                {feedbackLoading ? (
                                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                        <Loader2 className="h-4 w-4 animate-spin" /> Loading feedback…
                                    </div>
                                ) : feedback?.error ? (
                                    <div className="text-sm text-destructive">Couldn&apos;t load feedback for this session.</div>
                                ) : (!feedback?.mentor_feedback && !feedback?.student_feedback) ? (
                                    <div className="rounded-lg border border-dashed border-border/70 bg-muted/30 p-3 text-sm text-muted-foreground" data-testid="admin-booking-feedback-empty">
                                        No feedback submitted for this session yet.
                                    </div>
                                ) : (
                                    <div className="space-y-3">
                                        {/* Student feedback */}
                                        {feedback?.student_feedback && !isSubHidden('feedback_submissions', 'student_feedback') && (
                                            <div className="rounded-lg border border-border/60 bg-card p-3" data-testid="admin-booking-student-feedback">
                                                <div className="mb-2 flex items-center justify-between">
                                                    <span className="text-sm font-semibold">Student feedback</span>
                                                    <span className="text-xs text-muted-foreground">{formatDateTime(feedback.student_feedback.submitted_at)}</span>
                                                </div>
                                                <div className="space-y-2 text-sm">
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-muted-foreground">Rating</span>
                                                        <StarRating value={feedback.student_feedback.rating} />
                                                    </div>
                                                    {feedback.student_feedback.would_recommend !== null && feedback.student_feedback.would_recommend !== undefined && (
                                                        <div className="flex items-center gap-2">
                                                            <span className="text-muted-foreground">Would recommend</span>
                                                            {feedback.student_feedback.would_recommend ? (
                                                                <span className="inline-flex items-center gap-1 text-emerald-600"><ThumbsUp className="h-3.5 w-3.5" /> Yes</span>
                                                            ) : (
                                                                <span className="inline-flex items-center gap-1 text-destructive"><ThumbsDown className="h-3.5 w-3.5" /> No</span>
                                                            )}
                                                        </div>
                                                    )}
                                                    {feedback.student_feedback.comments && (
                                                        <div>
                                                            <div className="text-muted-foreground">Comments</div>
                                                            <div className="mt-0.5 whitespace-pre-wrap">{feedback.student_feedback.comments}</div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        )}

                                        {/* Mentor feedback */}
                                        {feedback?.mentor_feedback && !isSubHidden('feedback_submissions', 'mentor_feedback') && (
                                            <div className="rounded-lg border border-border/60 bg-card p-3" data-testid="admin-booking-mentor-feedback">
                                                <div className="mb-2 flex items-center justify-between">
                                                    <span className="text-sm font-semibold">Mentor feedback</span>
                                                    <span className="text-xs text-muted-foreground">{formatDateTime(feedback.mentor_feedback.submitted_at)}</span>
                                                </div>
                                                <div className="space-y-2 text-sm">
                                                    {(feedback.form?.fields || []).map((f) => (
                                                        <div key={f.id}>
                                                            <div className="text-muted-foreground">{f.label}</div>
                                                            <div className="mt-0.5">{renderAnswer(f, feedback.mentor_feedback.answers?.[f.id])}</div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        {selected && canEdit && selected.status === 'scheduled' && (
                            <Button variant="destructive" onClick={() => { cancel(selected.id); setSelected(null); }}>
                                <XCircle className="mr-1 h-4 w-4" /> Cancel booking
                            </Button>
                        )}
                        <Button variant="secondary" onClick={() => setSelected(null)}>Close</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <ManualBookingDialog
                open={manualOpen}
                onOpenChange={setManualOpen}
                onCreated={() => { setManualOpen(false); refresh(); }}
            />

            {/* Cancel with party dialog */}
            <CancelBookingDialog
                booking={cancelTarget}
                onClose={() => setCancelTarget(null)}
                onDone={() => { setCancelTarget(null); setSelected(null); refresh(); }}
            />

            {/* Admin reschedule dialog */}
            <AdminRescheduleDialog
                booking={rescheduleTarget}
                onClose={() => setRescheduleTarget(null)}
                onDone={() => { setRescheduleTarget(null); setSelected(null); refresh(); }}
            />
        </AdminLayout>
    );
}


function CancelBookingDialog({ booking, onClose, onDone }) {
    const [party, setParty] = useState('candidate');
    const [reason, setReason] = useState('');
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (booking) { setParty('candidate'); setReason(''); }
    }, [booking?.id]);

    const submit = async () => {
        setSubmitting(true);
        try {
            const { data } = await api.post(`/admin/bookings/${booking.id}/cancel`, { party, reason: reason.trim() || undefined });
            toast.success(data.credit_refunded ? 'Cancelled — credit refunded.' : 'Cancelled.');
            onDone();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Cancel failed.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={!!booking} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="sm:max-w-md" data-testid="admin-cancel-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display">Cancel session</DialogTitle>
                    <DialogDescription>
                        {booking && `${booking.mentor_name} · ${booking.student_name} · ${formatDateTime(booking.start_iso)}`}
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                    <div>
                        <div className="mb-1.5 text-sm font-medium">Who cancelled?</div>
                        <ToggleGroup type="single" variant="outline" value={party} onValueChange={(v) => v && setParty(v)} className="justify-start">
                            <ToggleGroupItem value="candidate" data-testid="cancel-party-candidate" className="gap-1 data-[state=on]:bg-primary data-[state=on]:text-primary-foreground">Candidate</ToggleGroupItem>
                            <ToggleGroupItem value="mentor" data-testid="cancel-party-mentor" className="gap-1 data-[state=on]:bg-primary data-[state=on]:text-primary-foreground">Mentor</ToggleGroupItem>
                        </ToggleGroup>
                    </div>
                    <div>
                        <div className="mb-1.5 text-sm font-medium">Reason (optional)</div>
                        <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. mentor unwell, candidate requested…" rows={2} />
                    </div>
                    <p className="text-xs text-muted-foreground">The student's credit will be refunded.</p>
                </div>
                <DialogFooter>
                    <Button variant="ghost" onClick={onClose}>Keep session</Button>
                    <Button
                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        disabled={submitting}
                        onClick={submit}
                        data-testid="cancel-confirm-button"
                    >
                        {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <XCircle className="mr-2 h-4 w-4" />}
                        Cancel as {party}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}


function AdminRescheduleDialog({ booking, onClose, onDone }) {
    const [party, setParty] = useState('candidate');
    const [date, setDate] = useState('');
    const [time, setTime] = useState('');
    const [reason, setReason] = useState('');
    const [submitting, setSubmitting] = useState(false);

    // Prefill the picker with the booking's current start (in the admin's local time).
    useEffect(() => {
        if (!booking) return;
        setParty('candidate');
        setReason('');
        const d = new Date(booking.start_iso);
        if (!isNaN(d.getTime())) {
            const pad = (n) => String(n).padStart(2, '0');
            setDate(`${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`);
            setTime(`${pad(d.getHours())}:${pad(d.getMinutes())}`);
        } else {
            setDate('');
            setTime('');
        }
    }, [booking?.id]);

    // Human-readable preview of the chosen new time.
    const preview = useMemo(() => {
        if (!date || !time) return '';
        const d = new Date(`${date}T${time}:00`);
        return isNaN(d.getTime()) ? '' : formatDateTime(d.toISOString());
    }, [date, time]);

    const submit = async () => {
        if (!date || !time) { toast.error('Pick a new date and time.'); return; }
        const newStart = new Date(`${date}T${time}:00`);
        if (isNaN(newStart.getTime())) { toast.error('Invalid date/time.'); return; }
        setSubmitting(true);
        try {
            await api.post(`/admin/bookings/${booking.id}/reschedule`, {
                start_iso: newStart.toISOString(),
                party,
                custom: true,
                reason: reason.trim() || undefined,
            });
            toast.success(`Rescheduled (${party}). Updated invite sent to both.`);
            onDone();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Reschedule failed.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={!!booking} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-h-[90vh] overflow-auto sm:max-w-lg" data-testid="admin-reschedule-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display">Reschedule session</DialogTitle>
                    <DialogDescription>
                        {booking && `${booking.mentor_name} · ${booking.student_name} · currently ${formatDateTime(booking.start_iso)}`}
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                    <div>
                        <div className="mb-1.5 text-sm font-medium">Who requested the reschedule?</div>
                        <ToggleGroup type="single" variant="outline" value={party} onValueChange={(v) => v && setParty(v)} className="justify-start">
                            <ToggleGroupItem value="candidate" data-testid="resched-party-candidate" className="data-[state=on]:bg-primary data-[state=on]:text-primary-foreground">Candidate</ToggleGroupItem>
                            <ToggleGroupItem value="mentor" data-testid="resched-party-mentor" className="data-[state=on]:bg-primary data-[state=on]:text-primary-foreground">Mentor</ToggleGroupItem>
                        </ToggleGroup>
                    </div>

                    <div>
                        <div className="mb-1.5 text-sm font-medium">Pick a new date &amp; time</div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="text-xs text-muted-foreground">Date</label>
                                <Input
                                    type="date"
                                    value={date}
                                    onChange={(e) => setDate(e.target.value)}
                                    className="mt-1"
                                    data-testid="resched-date-input"
                                />
                            </div>
                            <div>
                                <label className="text-xs text-muted-foreground">Time</label>
                                <Input
                                    type="time"
                                    value={time}
                                    onChange={(e) => setTime(e.target.value)}
                                    className="mt-1"
                                    data-testid="resched-time-input"
                                />
                            </div>
                        </div>
                        {preview && (
                            <p className="mt-2 text-xs text-muted-foreground" data-testid="resched-preview">
                                New session time: <span className="font-medium text-foreground">{preview}</span>
                                {booking?.duration_minutes ? ` · ${booking.duration_minutes} min` : ''}
                            </p>
                        )}
                    </div>

                    <div>
                        <div className="mb-1.5 text-sm font-medium">Reason (optional)</div>
                        <Textarea
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            placeholder="e.g. mentor requested a different time"
                            rows={2}
                            data-testid="resched-reason-input"
                        />
                    </div>

                    <div className="rounded-lg border border-border/60 bg-muted/30 p-3 text-xs text-muted-foreground">
                        <strong className="text-foreground">Admin override:</strong> you can pick any free-form date/time — this bypasses the mentor&apos;s gradnext availability slots. The Google Calendar event is moved and an updated invite (with the Meet link) is emailed to both the student and mentor.
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="ghost" onClick={onClose}>Cancel</Button>
                    <Button disabled={submitting || !date || !time} onClick={submit} data-testid="resched-confirm-button">
                        {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RotateCcw className="mr-2 h-4 w-4" />}
                        Reschedule as {party}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}


function ManualBookingDialog({ open, onOpenChange, onCreated }) {
    const [students, setStudents] = useState([]);
    const [mentors, setMentors] = useState([]);
    const [studentId, setStudentId] = useState('');
    const [mentorId, setMentorId] = useState('');
    const [date, setDate] = useState('');
    const [time, setTime] = useState('');
    const [duration, setDuration] = useState(30);
    const [topic, setTopic] = useState('');
    const [chargeCredit, setChargeCredit] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [loadingLookups, setLoadingLookups] = useState(false);

    const selectedStudent = students.find((s) => s.id === studentId);
    const selectedMentor = mentors.find((m) => m.id === mentorId);
    const mentorEmailMissing = selectedMentor && !selectedMentor.email;

    useEffect(() => {
        if (!open) return;
        (async () => {
            setLoadingLookups(true);
            try {
                const [s, m] = await Promise.all([
                    api.get('/admin/students'),
                    api.get('/mentors'),
                ]);
                setStudents(s.data.students || []);
                setMentors(m.data.mentors || []);
            } catch (e) {
                toast.error('Failed to load students/mentors');
            } finally {
                setLoadingLookups(false);
            }
        })();
        // reset form
        setStudentId('');
        setMentorId('');
        setDate('');
        setTime('');
        setDuration(30);
        setTopic('');
        setChargeCredit(true);
    }, [open]);

    const submit = async () => {
        if (!studentId) return toast.error('Select a student');
        if (!mentorId) return toast.error('Select a mentor');
        if (!date || !time) return toast.error('Pick a date and time');
        const isoLocal = new Date(`${date}T${time}:00`);
        if (isNaN(isoLocal.getTime())) return toast.error('Invalid date/time');
        setSubmitting(true);
        try {
            const payload = {
                mentor_id: mentorId,
                student_id: studentId,
                start_iso: isoLocal.toISOString(),
                duration_minutes: Number(duration),
                topic: topic.trim() || undefined,
                charge_credit: chargeCredit,
            };
            const { data } = await api.post('/admin/bookings/manual', payload);
            toast.success('Booking created and emails sent.');
            onCreated?.(data.booking);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to create booking');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent data-testid="admin-manual-booking-dialog" className="sm:max-w-lg">
                <DialogHeader>
                    <DialogTitle className="font-display text-xl">Add a session manually</DialogTitle>
                    <p className="text-sm text-muted-foreground">Create an off-platform booking. Confirmation emails + calendar invite will be sent to student and mentor.</p>
                </DialogHeader>

                {loadingLookups ? (
                    <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
                        <Loader2 className="h-4 w-4 animate-spin" /> Loading…
                    </div>
                ) : (
                    <div className="space-y-4">
                        <div>
                            <label className="text-sm font-medium">Student *</label>
                            <Select value={studentId} onValueChange={setStudentId}>
                                <SelectTrigger className="mt-1" data-testid="manual-student-select">
                                    <SelectValue placeholder="Select a student…" />
                                </SelectTrigger>
                                <SelectContent>
                                    {students.map((s) => (
                                        <SelectItem key={s.id} value={s.id}>
                                            {s.name} · {s.email} · {s.credits} credits
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        <div>
                            <label className="text-sm font-medium">Mentor *</label>
                            <Select value={mentorId} onValueChange={setMentorId}>
                                <SelectTrigger className="mt-1" data-testid="manual-mentor-select">
                                    <SelectValue placeholder="Select a mentor…" />
                                </SelectTrigger>
                                <SelectContent>
                                    {mentors.map((m) => (
                                        <SelectItem key={m.id} value={m.id}>
                                            {m.name}{m.domain ? ` · ${m.domain}` : ''}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="text-sm font-medium">Date *</label>
                                <Input
                                    type="date"
                                    value={date}
                                    onChange={(e) => setDate(e.target.value)}
                                    className="mt-1"
                                    data-testid="manual-date-input"
                                />
                            </div>
                            <div>
                                <label className="text-sm font-medium">Time *</label>
                                <Input
                                    type="time"
                                    value={time}
                                    onChange={(e) => setTime(e.target.value)}
                                    className="mt-1"
                                    data-testid="manual-time-input"
                                />
                            </div>
                        </div>

                        <div>
                            <label className="text-sm font-medium">Duration (minutes) *</label>
                            <Input
                                type="number"
                                min="15"
                                max="240"
                                step="15"
                                value={duration}
                                onChange={(e) => setDuration(e.target.value)}
                                className="mt-1"
                                data-testid="manual-duration-input"
                            />
                        </div>

                        <div>
                            <label className="text-sm font-medium">Topic <span className="text-muted-foreground">(optional)</span></label>
                            <Input
                                value={topic}
                                onChange={(e) => setTopic(e.target.value)}
                                placeholder="e.g. Consulting case prep"
                                className="mt-1"
                            />
                        </div>

                        <label className="flex items-center gap-2 rounded-lg border border-border/60 bg-secondary p-3 text-sm">
                            <input
                                type="checkbox"
                                checked={chargeCredit}
                                onChange={(e) => setChargeCredit(e.target.checked)}
                                className="h-4 w-4 accent-primary"
                                data-testid="manual-charge-credit"
                            />
                            <span>Deduct 1 credit from the student (uncheck for a comped session)</span>
                        </label>

                        {(selectedStudent || selectedMentor) && (
                            <div className="rounded-lg border border-border/60 bg-card p-3 text-xs">
                                <div className="mb-1 font-semibold uppercase tracking-wide text-muted-foreground">Emails will be sent to</div>
                                {selectedStudent && (
                                    <div className="flex items-center justify-between py-1">
                                        <span className="text-muted-foreground">Student</span>
                                        <span className="font-medium tabular-nums" data-testid="manual-recipient-student">{selectedStudent.email}</span>
                                    </div>
                                )}
                                {selectedMentor && (
                                    <div className="flex items-center justify-between py-1">
                                        <span className="text-muted-foreground">Mentor</span>
                                        {mentorEmailMissing ? (
                                            <span className="font-medium text-amber-700" data-testid="manual-recipient-mentor">
                                                No email on file
                                            </span>
                                        ) : (
                                            <span className="font-medium tabular-nums" data-testid="manual-recipient-mentor">{selectedMentor.email}</span>
                                        )}
                                    </div>
                                )}
                                {mentorEmailMissing && (
                                    <p className="mt-2 rounded border border-amber-200 bg-amber-50 p-2 text-amber-900">
                                        This mentor has no email set — the mentor confirmation will be skipped. Add one under <strong>Mentors → Edit card → Mentor email</strong> first, or continue and only notify the student.
                                    </p>
                                )}
                            </div>
                        )}

                        <div className="rounded-lg border border-border/60 bg-muted/30 p-3 text-xs text-muted-foreground">
                            <strong className="text-foreground">Note:</strong> Manual bookings bypass the gradnext platform, so this session will not appear on the mentor&apos;s gradnext dashboard. The mentor is notified via email + Google Calendar invite only.
                        </div>
                    </div>
                )}

                <DialogFooter>
                    <Button variant="secondary" onClick={() => onOpenChange(false)}>Cancel</Button>
                    <Button
                        onClick={submit}
                        disabled={submitting || loadingLookups}
                        data-testid="manual-submit-button"
                    >
                        {submitting ? (
                            <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Creating…</>
                        ) : (
                            <><CalendarIcon className="mr-2 h-4 w-4" /> Create booking</>
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
